import { apiCall } from '../helper/Common';

// Campaign API service using optimized apiCall function
// All backend APIs return: { success: boolean, message: string, data: any }
export const campaignAPI = {
  /**
   * Get all campaigns (simple list) with pagination support
   * GET /api/campaigns?client_id=1&page=1&limit=10
   * Returns: { success: true, message: string, data: array, pagination: object }
   */
  async getCampaigns(clientId = 1, page = 1, limit = 10) {
    try {
      const queryParams = new URLSearchParams({
        client_id: clientId.toString(),
        page: page.toString(),
        limit: limit.toString()
      });
      const response = await apiCall(`campaigns?${queryParams.toString()}`, 'GET');
      // Backend returns { success, message, data, pagination }
      if (response.success && response.data) {
        return response;
      }
      // Handle error response
      throw new Error(response.message || 'Failed to fetch campaigns');
    } catch (error) {
      console.error('Error in getCampaigns:', error);
      throw error;
    }
  },

  /**
   * Get filtered campaigns with pagination, sorting, and filters
   * POST /api/campaigns/filtered
   * Body: { client_id, page, limit, search, sorting: { field, direction }, filters: { status, type, Goal } }
   * Returns: { success: true, message: string, data: array, pagination: object }
   */
  async getFilteredCampaigns(filters = {}) {
    try {
      const payload = {
        client_id: filters.client_id || 1,
        page: filters.page || 1,
        limit: filters.limit || 50,
        search: filters.search || '',
        sorting: filters.sorting || { field: 'campaign_name', direction: 'asc' },
        filters: filters.filters || {}
      };
      
      const response = await apiCall('campaigns/filtered', 'POST', payload);
      // Backend returns { success, message, data, pagination }
      if (response.success && Array.isArray(response.data)) {
        return response;
      }
      throw new Error(response.message || 'Failed to fetch filtered campaigns');
    } catch (error) {
      console.error('Error in getFilteredCampaigns:', error);
      throw error;
    }
  },

  /**
   * Get single campaign by ID with rewards
   * GET /api/campaigns/:id
   * Returns: { success: true, message: string, data: { campaign: object, rewards: array } }
   */
  async getCampaign(id) {
    try {
      const response = await apiCall(`campaigns/${id}`, 'GET');
      // Backend returns { success, message, data: { campaign, rewards } }
      if (response.success && response.data) {
        return response;
      }
      throw new Error(response.message || 'Campaign not found');
    } catch (error) {
      console.error('Error in getCampaign:', error);
      throw error;
    }
  },

  /**
   * Create new campaign
   * POST /api/campaigns
   * Body: campaign data object
   * Returns: { success: true, message: string }
   */
  async createCampaign(campaignData) {
    try {
      const dataWithClientId = {
        ...campaignData,
        client_id: campaignData.client_id || 1
      };
      
      const response = await apiCall('campaigns', 'POST', dataWithClientId);
      // Backend returns { success, message }
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to create campaign');
    } catch (error) {
      console.error('Error in createCampaign:', error);
      throw error;
    }
  },

  /**
   * Update existing campaign
   * PUT /api/campaigns/:id
   * Body: campaign data object
   * Returns: { success: true, message: string }
   */
  async updateCampaign(id, campaignData) {
    try {
      const dataWithClientId = {
        ...campaignData,
        client_id: campaignData.client_id || 1
      };
      
      const response = await apiCall(`campaigns/${id}`, 'PUT', dataWithClientId);
      // Backend returns { success, message }
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to update campaign');
    } catch (error) {
      console.error('Error in updateCampaign:', error);
      throw error;
    }
  },

  /**
   * Delete campaign (soft delete)
   * DELETE /api/campaigns/:id
   * Returns: { success: true, message: string }
   */
  async deleteCampaign(id) {
    try {
      const response = await apiCall(`campaigns/${id}`, 'DELETE');
      // Backend returns { success, message }
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to delete campaign');
    } catch (error) {
      console.error('Error in deleteCampaign:', error);
      throw error;
    }
  },

  /**
   * Update campaign status
   * PUT /api/campaigns/:id
   * Body: { campaign_status, client_id }
   * Returns: { success: true, message: string }
   */
  async updateCampaignStatus(id, status, clientId = 1) {
    try {
      const response = await apiCall(`campaigns/${id}`, 'PUT', { 
        campaign_status: status,
        client_id: clientId 
      });
      // Backend returns { success, message }
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to update campaign status');
    } catch (error) {
      console.error('Error in updateCampaignStatus:', error);
      throw error;
    }
  },

  /**
   * Bulk delete campaigns
   * POST /api/campaigns/bulk-delete
   * Body: { campaignIds: array, client_id }
   * Returns: { success: true, message: string, data: object }
   */
  async bulkDeleteCampaigns(campaignIds, clientId = 1) {
    try {
      const response = await apiCall('campaigns/bulk-delete', 'POST', { 
        campaignIds: campaignIds,
        client_id: clientId 
      });
      // Backend returns { success, message, data }
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to delete campaigns');
    } catch (error) {
      console.error('Error in bulkDeleteCampaigns:', error);
      throw error;
    }
  },

  /**
   * Save or update settings
   * POST /api/settings
   * Body: { client_id, crowdfunding_settings, presales_settings, product_drop_settings }
   * Returns: { success: true, message: string, data: object }
   */
  async saveSettings(settingsData) {
    try {
      const response = await apiCall('settings', 'POST', settingsData);
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to save settings');
    } catch (error) {
      console.error('Error in saveSettings:', error);
      throw error;
    }
  },

  /**
   * Get settings for a client
   * GET /api/settings?client_id=1
   * Returns: { success: true, message: string, data: object }
   */
  async getSettings(clientId = 1) {
    try {
      const response = await apiCall(`settings?client_id=${clientId}`, 'GET');
      if (response.success) {
        return response;
      }
      throw new Error(response.message || 'Failed to get settings');
    } catch (error) {
      console.error('Error in getSettings:', error);
      throw error;
    }
  }
};

